<script setup lang="ts">
const { filters } = storeToRefs(useSearchStore())

const props = withDefaults(defineProps<{
  isCrossedSearch?: boolean
}>(), {
  isCrossedSearch: false,
})

const emits = defineEmits<{
  close: []
}>()

const { t } = useI18n()

const travelersOptions = [{
  text: t('components.app_modal_search_quick_search.travelers.couple'),
  value: 2,
}, {
  text: t('components.app_modal_search_quick_search.travelers.family'),
  value: 3,
}, {
  text: t('components.app_modal_search_quick_search.travelers.tribe'),
  value: 6,
}]

const petsOptions = [{
  text: t('components.app_modal_search_quick_search.pets.with'),
  value: true,
}, {
  text: t('commons.options.no'),
  value: false,
}]

const { getTranslatedVehicleEquipment } = useGetTranslatedVehicleEquipment()
const equipmentsOptions = computed(() => {
  const equipements = ['shower_int', 'wc', 'bicycle', 'fridge', 'ac']
  if (!props.isCrossedSearch) {
    equipements.push('baby_seat')
  }
  return equipements.map(value => ({
    value,
    text: getTranslatedVehicleEquipment(value) || '',
  }))
})

const internationalOptions = [{
  text: t('commons.options.yes'),
  value: true,
}, {
  text: t('components.app_modal_search_quick_search.international.stay'),
  value: false,
}]

const initialData = () => ({
  seatbelts: undefined,
  pets: undefined,
  equipments: [],
  international: undefined,
})

const { currentStep, nextStep, prevStep, isLastStep } = useModalSteps({ numberOfSteps: 4 })

const form = ref<{
  seatbelts?: number
  pets?: boolean
  equipments: string[]
  international?: boolean
}>(initialData())

const route = useRoute()

const submit = async () => {
  const locationDatesQuery: Partial<Pick<SearchFilters, 'where' | 'latitude' | 'longitude' | 'date_from' | 'date_to' | 'radius'>> = {
    where: queryValueToString(route.query.where),
    latitude: queryValueToNumber(route.query.latitude),
    longitude: queryValueToNumber(route.query.longitude),
    date_from: queryValueToString(route.query.longitude),
    date_to: queryValueToString(route.query.longitude),
    radius: queryValueToNumber(route.query.radius),
  }
  const params = { ...initialSearchFiltersFactory(), ...locationDatesQuery }
  const { seatbelts, pets, equipments, international } = form.value
  if (seatbelts !== undefined) {
    params.seatbelts = seatbelts
    params.beds = seatbelts
  }
  if (pets) {
    params.pets = true
  }
  if (international) {
    params.international = true
  }
  params.equipments = equipments
  filters.value = params
  const { navigateToFilters } = useSearchNavigateToFilters()
  await navigateToFilters()
  emits('close')
}

const skip = () => {
  switch (currentStep.value) {
    case 0:
      form.value.seatbelts = initialData().seatbelts
      nextStep()
      break
    case 1:
      form.value.pets = initialData().pets
      nextStep()
      break
    case 2:
      form.value.equipments = initialData().equipments
      nextStep()
      break
    case 3:
      form.value.international = initialData().international
      submit()
  }
}
</script>

<template>
  <YscModal @close="emits('close')">
    <template #header>
      <AppModalStepsHeader
        :current-step="currentStep"
        :title="t('components.app_modal_search_quick_search.title')"
        @prev-step="prevStep()"
        @close="emits('close')"
      />
    </template>
    <!-- Travelers -->
    <YscInputRadioGroup
      v-if="currentStep === 0"
      id="travelers"
      v-model="form.seatbelts"
      name="travelers"
      :options="travelersOptions"
      layout-column
    >
      {{ t('components.app_modal_search_quick_search.travelers.label') }}
    </YscInputRadioGroup>

    <!-- Pets -->
    <YscInputRadioGroup
      v-else-if="currentStep === 1"
      id="pets"
      v-model="form.pets"
      name="pets"
      :options="petsOptions"
      layout-column
    >
      {{ t('components.app_modal_search_quick_search.pets.label') }}
    </YscInputRadioGroup>

    <!-- EQUIPMENTS -->
    <YscInputCheckboxGroup
      v-else-if="currentStep === 2"
      v-model="form.equipments"
      name="equipments"
      :options="equipmentsOptions"
      layout-column
    >
      {{ t('components.app_modal_search_quick_search.equipments.label') }}
    </YscInputCheckboxGroup>

    <!-- international -->
    <YscInputRadioGroup
      v-else-if="currentStep === 3"
      id="international"
      v-model="form.international"
      name="international"
      :options="internationalOptions"
      layout-column
    >
      {{ t('components.app_modal_search_quick_search.international.label') }}
    </YscInputRadioGroup>

    <template #footer>
      <div class="flex items-center justify-between">
        <button
          class="link text-gray-500"
          @click="skip"
        >
          {{ t('commons.actions.skip') }}
        </button>

        <button
          v-if="!isLastStep"
          class="btn btn-primary"
          @click="nextStep"
        >
          {{ $t('commons.actions.next') }}
        </button>
        <button
          v-else
          class="btn btn-primary"
          @click="submit"
        >
          {{ $t('commons.actions.validate') }}
        </button>
      </div>
    </template>
  </YscModal>
</template>
